import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { toast } from "react-toastify";
import ViewerNavbar from "./ViewerNavbar";

import ReportPanel from "./ReportPanel";
import ScrollableLayoutRenderer from "./ScrollableLayoutRenderer";
import API, { ensureOrthancAuthCookie } from "../../services/api.ts";
import "./DvViewer.css";
import { resolveAllowedViewerUrl, isSlicerViewerUrl } from '../../utils/safeUrl';

const DvViewer = () => {
    // Top-level dispatcher: if custom_viewer_id is in the URL, render the
    // scrollable runtime; otherwise the legacy iframe viewer. This must be a
    // proper component branch (not an early return inside the iframe component)
    // so hook order stays stable across renders.
    const urlParams = new URLSearchParams(window.location.search);
    const customViewerIdParam = urlParams.get('custom_viewer_id');
    if (customViewerIdParam) {
        return <DvViewerScrollable customViewerId={customViewerIdParam} />;
    }
    return <DvViewerIframe />;
};

const DvViewerScrollable = ({ customViewerId }) => {
    const urlParams = new URLSearchParams(window.location.search);
    const studyId = urlParams.get('study_id') || localStorage.getItem('studyId');
    const patientIdParam = urlParams.get('patient_id');
    const patientIdsParam = urlParams.get('patient_ids');
    const currentIndexParam = urlParams.get('current_index');

    // Build the patient list from URL params. Multi-patient launch comes from
    // the study table's "select multiple patients + launch custom viewer" path.
    const patientIds = useMemo(() => {
        if (patientIdsParam) return patientIdsParam.split(',').filter(Boolean);
        if (patientIdParam) return [patientIdParam];
        return [];
    }, [patientIdsParam, patientIdParam]);

    const [currentPatientIndex, setCurrentPatientIndex] = useState(() => {
        const n = parseInt(currentIndexParam, 10);
        if (Number.isFinite(n) && n >= 0 && n < patientIds.length) return n;
        return 0;
    });

    // Fetch study info so the top navbar gets the study's labels/questions/
    // scores/share_annotations/viewer_navbar_config — same data the legacy
    // iframe path fetches at DvViewerIframe.fetchStudyInfo.
    const [studyInfo, setStudyInfo] = useState({
        name: "",
        labels: [],
        labelDisplayMode: "dropdown",
        questions: [],
        scores: [],
        shareAnnotations: false,
        viewerNavbarConfig: {},
    });
    useEffect(() => {
        if (!studyId) return;
        let cancelled = false;
        API.get(`/studies/${studyId}/`).then((resp) => {
            if (cancelled) return;
            const d = resp.data || {};
            setStudyInfo({
                name: d.name || "",
                labels: d.labels || [],
                labelDisplayMode: d.label_display_mode || "dropdown",
                questions: d.questions || [],
                scores: d.scores || [],
                shareAnnotations: d.share_annotations || false,
                viewerNavbarConfig: d.viewer_navbar_config || {},
            });
        }).catch(() => {
            // Non-fatal: the renderer still works without navbar config.
        });
        return () => { cancelled = true; };
    }, [studyId]);

    const activePatientId = patientIds[currentPatientIndex] || patientIdParam;

    const handlePatientChange = useCallback((newIndex) => {
        if (newIndex < 0 || newIndex >= patientIds.length) return;
        if (newIndex === currentPatientIndex) return;
        setCurrentPatientIndex(newIndex);
        // Keep URL in sync for refresh/share — patient_id reflects the active one.
        const url = new URL(window.location.href);
        url.searchParams.set('current_index', newIndex.toString());
        url.searchParams.set('patient_id', patientIds[newIndex]);
        window.history.replaceState({}, '', url.toString());
    }, [patientIds, currentPatientIndex]);

    // The in-layout Nav component (queue mode) calls this when it picks a new
    // patient. If the patient is in our local selected set, just shift the
    // active index. Otherwise (the common reader-queue case — patientIds is
    // [singleId] and the next case is a different id), do a full navigation:
    // `patientIds` is memoized from URL params at mount, so a history
    // replaceState wouldn't trigger a re-render. `window.location.href` does.
    const handleQueuePatientChange = useCallback((newPatientId) => {
        const idx = patientIds.indexOf(newPatientId);
        if (idx >= 0) {
            handlePatientChange(idx);
            return;
        }
        const url = new URL(window.location.href);
        url.searchParams.set('patient_id', newPatientId);
        url.searchParams.delete('patient_ids');
        url.searchParams.delete('current_index');
        window.location.href = url.toString();
    }, [patientIds, handlePatientChange]);

    return (
        <div className="dv-viewer-page">
            <ViewerNavbar
                studyId={studyId}
                studyName={studyInfo.name}
                labels={studyInfo.labels}
                labelDisplayMode={studyInfo.labelDisplayMode}
                questions={studyInfo.questions}
                scores={studyInfo.scores}
                patientId={activePatientId}
                imageId={null}
                shareAnnotations={studyInfo.shareAnnotations}
                studyInstanceUID={null}
                patientIds={patientIds}
                currentPatientIndex={currentPatientIndex}
                onPatientChange={handlePatientChange}
                isNavigating={false}
                viewerNavbarConfig={studyInfo.viewerNavbarConfig}
            />
            <div className="dv-viewer-content">
                <div className="dv-viewer-main">
                    <ScrollableLayoutRenderer
                        customViewerId={customViewerId}
                        studyId={studyId}
                        patientId={activePatientId}
                        patientIds={patientIds}
                        onPatientChange={handleQueuePatientChange}
                    />
                </div>
            </div>
        </div>
    );
};

const DvViewerIframe = () => {
    // Get parameters from URL
    const urlParams = new URLSearchParams(window.location.search);
    const viewerUrlParam = urlParams.get('viewer_url');
    const studyIdParam = urlParams.get('study_id');
    const patientIdParam = urlParams.get('patient_id');
    const imageIdParam = urlParams.get('image_id');
    const patientIdsParam = urlParams.get('patient_ids');
    const studyUidsParam = urlParams.get('study_uids');
    const trameSessionKeyParam = urlParams.get('trame_session_key');

    const [studyId] = useState(studyIdParam || localStorage.getItem('studyId'));
    const [studyName, setStudyName] = useState("");
    const [labels, setLabels] = useState([]);
    const [labelDisplayMode, setLabelDisplayMode] = useState('dropdown');
    const [questions, setQuestions] = useState([]);
    const [scores, setScores] = useState([]);
    const [shareAnnotations, setShareAnnotations] = useState(false);
    const [viewerNavbarConfig, setViewerNavbarConfig] = useState({});

    // Multi-patient navigation state
    const patientIds = useMemo(() => {
        if (patientIdsParam) return patientIdsParam.split(',');
        if (patientIdParam) return [patientIdParam];
        return [];
    }, [patientIdsParam, patientIdParam]);

    const studyUids = useMemo(() => {
        if (studyUidsParam) return studyUidsParam.split(',');
        return [];
    }, [studyUidsParam]);

    // Trame-slicer multi-patient URLs stored in sessionStorage to avoid URL length limits
    const [trameUrls] = useState(() => {
        if (trameSessionKeyParam) {
            try {
                const stored = sessionStorage.getItem(trameSessionKeyParam);
                if (stored) return JSON.parse(stored);
            } catch (_) {}
        }
        return [];
    });

    // True when the current session is a trame-slicer viewer (not OHIF)
    // Parsed-path check, not a substring match: `https://evil.example/slicer/`
    // and `javascript:/*/slicer/*/alert(1)` both satisfied the old `.includes()`.
    const isTrame = trameUrls.length > 0 || isSlicerViewerUrl(viewerUrlParam);

    // Signal the trame worker to save immediately before navigating away.
    // The worker polls for .save_signal every 100 ms; we wait 200 ms to be safe.
    const triggerTrameSave = useCallback(async (pid) => {
        if (!isTrame || !pid) return;
        try {
            await API.post('/viewers/trigger-trame-save/', { patient_id: pid });
            await new Promise(resolve => setTimeout(resolve, 200));
        } catch (_) {}
    }, [isTrame]);

    // Debug: Log the patient-to-studyUID mapping on load
    useEffect(() => {
        console.log('%c[DvViewer] Session Mapping Table', 'font-weight: bold; font-size: 14px; color: blue;');
        console.log('[DvViewer] Raw URL params:');
        console.log('  patient_ids:', patientIdsParam);
        console.log('  study_uids:', studyUidsParam);
        console.log('[DvViewer] Parsed arrays:');
        console.log('  patientIds:', patientIds);
        console.log('  studyUids:', studyUids);
        console.log('[DvViewer] Array lengths - patientIds:', patientIds.length, ', studyUids:', studyUids.length);
        console.table(
            patientIds.map((pid, idx) => ({
                Index: idx,
                PatientID: pid,
                StudyUID: studyUids[idx] ? `...${studyUids[idx].slice(-12)}` : 'N/A',
                FullStudyUID: studyUids[idx] || 'N/A'
            }))
        );
        console.log('[DvViewer] Current session key:', patientIdsParam || patientIdParam || 'none');
    }, [patientIds, studyUids, patientIdsParam, patientIdParam]);

    // Initialize currentPatientIndex from URL parameter or default to 0
    // We use sessionStorage (not localStorage) to preserve index only within the same tab/session
    // A new viewer window should always start at index 0 unless current_index is in URL
    const [currentPatientIndex, setCurrentPatientIndex] = useState(() => {
        // First check URL parameter - this is the authoritative source
        const indexParam = urlParams.get('current_index');
        if (indexParam) {
            const idx = parseInt(indexParam, 10);
            const pIds = patientIdsParam ? patientIdsParam.split(',') : (patientIdParam ? [patientIdParam] : []);
            if (!isNaN(idx) && idx >= 0 && idx < pIds.length) {
                console.log(`[DvViewer] Starting at index ${idx} from URL parameter`);
                return idx;
            }
        }

        // For page refreshes within same tab, check sessionStorage
        // sessionStorage is tab-specific so it won't leak between viewer windows
        const currentSessionKey = patientIdsParam || patientIdParam || '';
        const storedSessionKey = sessionStorage.getItem('viewerSessionKey');

        if (currentSessionKey === storedSessionKey) {
            const storedIndex = sessionStorage.getItem('currentPatientIndex');
            if (storedIndex) {
                const idx = parseInt(storedIndex, 10);
                const pIds = patientIdsParam ? patientIdsParam.split(',') : (patientIdParam ? [patientIdParam] : []);
                if (!isNaN(idx) && idx >= 0 && idx < pIds.length) {
                    console.log(`[DvViewer] Restoring index ${idx} from session refresh`);
                    return idx;
                }
            }
        }

        // New viewer session - always start at index 0
        console.log('[DvViewer] New viewer session, starting at index 0');
        sessionStorage.setItem('viewerSessionKey', currentSessionKey);
        return 0;
    });

    // Save currentPatientIndex to sessionStorage when it changes (for page refresh support)
    useEffect(() => {
        sessionStorage.setItem('currentPatientIndex', currentPatientIndex.toString());
    }, [currentPatientIndex]);

    // Ensure currentPatientIndex is within bounds when patientIds changes
    useEffect(() => {
        if (patientIds.length > 0 && currentPatientIndex >= patientIds.length) {
            setCurrentPatientIndex(0);
        }
    }, [patientIds, currentPatientIndex]);

    // Current patient and image based on navigation
    const patientId = patientIds[currentPatientIndex] || patientIdParam || localStorage.getItem('patientId');

    // For multi-patient mode, don't use a static imageId from URL since each patient has different images
    // The backend will look up the correct image for the current patient_id
    const isMultiPatient = patientIds.length > 1;
    const imageId = isMultiPatient ? null : (imageIdParam || localStorage.getItem('imageId'));

    // Build viewer URL for current patient
    const viewerUrl = useMemo(() => {
        if (!viewerUrlParam) return null;

        // Trame-slicer multi-patient: each patient has its own distinct URL.
        // These come from sessionStorage, which a crafted link also controls, so
        // they go through the same allowlist as the query-string value.
        if (trameUrls.length > 1 && currentPatientIndex < trameUrls.length) {
            return resolveAllowedViewerUrl(trameUrls[currentPatientIndex]);
        }

        // Allowlist before use -- this value comes straight from the query string.
        const decodedUrl = resolveAllowedViewerUrl(decodeURIComponent(viewerUrlParam));
        if (!decodedUrl) return null;

        // OHIF multi-patient: swap StudyInstanceUIDs for the current patient
        if (studyUids.length > 1 && currentPatientIndex < studyUids.length) {
            const currentStudyUid = studyUids[currentPatientIndex];
            // Re-check after the rewrite: decodedUrl is already allowlisted, but
            // the substitution edits the string, so validate the result too.
            const updatedUrl = resolveAllowedViewerUrl(decodedUrl.replace(
                /StudyInstanceUIDs=[^&]*/,
                `StudyInstanceUIDs=${currentStudyUid}`
            ));
            console.log(`[DvViewer] Patient ${currentPatientIndex} (${patientIds[currentPatientIndex]}): using StudyUID ${currentStudyUid}`);
            return updatedUrl;
        }

        // Single patient mode - use URL as-is
        return decodedUrl;
    }, [viewerUrlParam, studyUids, currentPatientIndex, patientIds, trameUrls]);

    // Report panel state
    const [showReport, setShowReport] = useState(false);
    const [reportUrl] = useState("");

    // Fetch study info including labels, questions, and scores
    const fetchStudyInfo = useCallback(async (retries = 3) => {
        if (!studyId) return;

        for (let attempt = 1; attempt <= retries; attempt++) {
            try {
                const response = await API.get(`/studies/${studyId}/`);
                setStudyName(response.data.name);
                setLabels(response.data.labels || []);
                setLabelDisplayMode(response.data.label_display_mode || 'dropdown');
                setQuestions(response.data.questions || []);
                setScores(response.data.scores || []);
                setShareAnnotations(response.data.share_annotations || false);
                setViewerNavbarConfig(response.data.viewer_navbar_config || {});
                return; // Success
            } catch (err) {
                console.error(`Error fetching study info (attempt ${attempt}/${retries}):`, err);
                if (attempt < retries) {
                    // Wait before retrying (1s, 2s, 3s)
                    await new Promise(resolve => setTimeout(resolve, attempt * 1000));
                }
            }
        }
    }, [studyId]);

    useEffect(() => {
        fetchStudyInfo();
    }, [fetchStudyInfo]);

    // Ensure orthanc_auth cookie is fresh when component mounts or patient changes
    const [authReady, setAuthReady] = useState(false);
    useEffect(() => {
        const refreshAuth = async () => {
            try {
                await ensureOrthancAuthCookie();
                console.log('[DvViewer] Auth cookie refreshed');
            } catch (err) {
                console.warn('[DvViewer] Could not refresh auth cookie:', err);
            }
            setAuthReady(true);
        };
        setAuthReady(false);
        refreshAuth();
    }, [currentPatientIndex]);

    // Ref for the OHIF iframe and its container
    const iframeRef = useRef(null);
    const containerRef = useRef(null);

    // Workaround for OHIF button state issue: trigger layout changes to force OHIF recalculation
    // This fixes issues where buttons (like Export DICOM SEG) stay disabled until
    // the viewport is resized (e.g., by opening dev tools)
    // Since cross-origin prevents us from dispatching events into the iframe,
    // we actually resize the iframe container to trigger OHIF's ResizeObserver
    const handleIframeLoad = useCallback(() => {
        console.log('[DvViewer] OHIF iframe loaded, triggering resize workaround');

        // Re-enable patient navigation once the new iframe has actually loaded.
        isNavigatingRef.current = false;
        setIsNavigating(false);

        const triggerResize = () => {
            const container = containerRef.current;
            if (!container) return;

            // Get the container's computed style
            const computedStyle = window.getComputedStyle(container);
            const currentPadding = computedStyle.paddingRight;

            // Add a tiny bit of padding to trigger reflow, then remove it
            container.style.paddingRight = '1px';

            // Use double RAF to ensure the browser has time to process the change
            requestAnimationFrame(() => {
                requestAnimationFrame(() => {
                    container.style.paddingRight = currentPadding === '0px' ? '' : currentPadding;
                    console.log('[DvViewer] Resize workaround applied');
                });
            });
        };

        // Apply resize at various intervals to catch different initialization stages
        // OHIF may take time to fully initialize its components
        setTimeout(triggerResize, 1500);
        setTimeout(triggerResize, 3000);
        setTimeout(triggerResize, 5000);
        setTimeout(triggerResize, 8000);
    }, []);

    // Handle keyboard shortcuts for labels
    useEffect(() => {
        const handleKeyPress = (e) => {
            // Ignore if typing in input
            if (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA') return;

            const key = e.key.toUpperCase();
            const matchingLabel = labels.find(l => l.shortcut === key);

            if (matchingLabel) {
                console.log(`Label "${matchingLabel.name}" activated via shortcut ${key}`);
                // Future: Send label event to iframe or backend
            }
        };

        window.addEventListener('keypress', handleKeyPress);
        return () => window.removeEventListener('keypress', handleKeyPress);
    }, [labels]);

    // Handle patient navigation in-place. Full page reloads cause auth-cookie
    // refresh races + OHIF cold-start floods when users click next rapidly; state
    // swap + iframe key remount gives OHIF a fresh iframe without reloading React
    // or re-running the cookie refresh multiple times.
    const [isNavigating, setIsNavigating] = useState(false);
    const isNavigatingRef = useRef(false);
    const handlePatientChange = useCallback(async (newIndex) => {
        if (isNavigatingRef.current) return;
        if (newIndex < 0 || newIndex >= patientIds.length) return;
        if (newIndex === currentPatientIndex) return;

        // Lock navigation immediately so rapid clicks don't double-fire.
        isNavigatingRef.current = true;
        setIsNavigating(true);

        // Flush trame worker state before the iframe is replaced.
        await triggerTrameSave(patientIds[currentPatientIndex]);

        const newPatientId = patientIds[newIndex];
        const newUrl = new URL(window.location.href);
        newUrl.searchParams.set('current_index', newIndex.toString());
        newUrl.searchParams.set('patient_id', newPatientId);

        if (trameUrls.length > 1 && newIndex < trameUrls.length) {
            // Trame-slicer: viewer_url in the address bar points to the new patient's URL
            newUrl.searchParams.set('viewer_url', encodeURIComponent(trameUrls[newIndex]));
        } else {
            // OHIF: swap StudyInstanceUIDs in the existing viewer URL
            const newStudyUid = studyUids[newIndex];
            const decodedViewerUrl = decodeURIComponent(viewerUrlParam);
            const updatedViewerUrl = decodedViewerUrl.replace(
                /StudyInstanceUIDs=[^&]*/,
                `StudyInstanceUIDs=${newStudyUid}`
            );
            newUrl.searchParams.set('viewer_url', encodeURIComponent(updatedViewerUrl));
        }

        window.history.replaceState({}, '', newUrl.toString());
        console.log(`[DvViewer] In-place navigation to patient ${newIndex} (${newPatientId})`);
        setCurrentPatientIndex(newIndex);
    }, [patientIds, studyUids, viewerUrlParam, currentPatientIndex, trameUrls]);

    // Flush trame state before leaving this page (back button, logo click).
    const handleBeforeLeave = useCallback(async () => {
        await triggerTrameSave(patientIds[currentPatientIndex]);
    }, [triggerTrameSave, patientIds, currentPatientIndex]);

    // Toggle vertical orientation for the current trame patient. Some
    // arthrex XA/RF acquisitions store pixel data anatomically inverted and
    // no DICOM tag distinguishes them, so the user flips manually. The
    // backend flips the cached NIfTI + markups in-place and we reload.
    const [flipping, setFlipping] = useState(false);
    const handleToggleOrientation = useCallback(async () => {
        const pid = patientIds[currentPatientIndex];
        if (!pid) return;
        if (!window.confirm(
            "Flip this patient's viewer vertically?\n\n"
            + "All existing measurements will be deleted. Segmentations will "
            + "remain but may not visually align with the new orientation and "
            + "may need to be redrawn."
        )) return;
        setFlipping(true);
        try {
            // Flush anything in the current trame session so the seg files on
            // disk are current before we flip the underlying NIfTI.
            await triggerTrameSave(pid);
            const key = `trame_orient_${pid}`;
            const current = sessionStorage.getItem(key) === '1';
            const next = !current;
            await API.post('/viewers/toggle-trame-orientation/', {
                patient_id: pid,
                skip_y_flip: next,
            });
            sessionStorage.setItem(key, next ? '1' : '0');
            // Full page reload — the trame URL stays the same but the NIfTI
            // it points to has been rewritten. The 60-second worker idle
            // timeout means the OLD worker may still be alive after reload,
            // but the trame-slicer stale-cleanup fix scopes deletions to
            // files this worker owned at load time, so the new worker's
            // fresh segs are safe.
            window.location.reload();
        } catch (err) {
            console.error('[DvViewer] toggle orientation failed:', err);
            toast.error('Failed to flip orientation.');
            setFlipping(false);
        }
    }, [patientIds, currentPatientIndex, triggerTrameSave]);

    // Best-effort save on window/tab close. sendBeacon is fire-and-forget;
    // the 1-second periodic save covers the remaining window.
    useEffect(() => {
        if (!isTrame) return;
        const onUnload = () => {
            const pid = patientIds[currentPatientIndex];
            if (!pid) return;
            const apiBase = (process.env.REACT_APP_API_URL || '/api/').replace(/\/$/, '');
            const token = localStorage.getItem('access_token');
            // sendBeacon doesn't support custom headers; use sync XHR as fallback.
            try {
                const xhr = new XMLHttpRequest();
                xhr.open('POST', `${apiBase}/viewers/trigger-trame-save/`, false);
                xhr.setRequestHeader('Content-Type', 'application/json');
                if (token) xhr.setRequestHeader('Authorization', `Bearer ${token}`);
                xhr.send(JSON.stringify({ patient_id: pid }));
            } catch (_) {}
        };
        window.addEventListener('beforeunload', onUnload);
        return () => window.removeEventListener('beforeunload', onUnload);
    }, [isTrame, patientIds, currentPatientIndex]);

    // Clear the nav guard once the freshly-mounted iframe reports loaded, or after
    // a 4s safety timeout in case cross-origin onLoad never fires reliably.
    useEffect(() => {
        if (!isNavigating) return;
        const safety = setTimeout(() => {
            isNavigatingRef.current = false;
            setIsNavigating(false);
        }, 4000);
        return () => clearTimeout(safety);
    }, [isNavigating, currentPatientIndex]);

    // Close report panel
    const handleCloseReport = () => {
        setShowReport(false);
    };

    return (
        <div className="dv-viewer-page">
            {flipping && (
                <div style={{
                    position: 'fixed',
                    inset: 0,
                    background: 'rgba(0,0,0,0.75)',
                    zIndex: 10000,
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: 'white',
                    fontSize: '1.1rem',
                }}>
                    <div className="spinner-border text-light mb-3" role="status" style={{ width: '3rem', height: '3rem' }}>
                        <span className="visually-hidden">Loading...</span>
                    </div>
                    <div>Flipping volume and updating measurements…</div>
                    <div style={{ fontSize: '0.9rem', opacity: 0.8, marginTop: 8 }}>
                        Please wait, the page will reload automatically.
                    </div>
                </div>
            )}
            <ViewerNavbar
                studyId={studyId}
                studyName={studyName}
                labels={labels}
                labelDisplayMode={labelDisplayMode}
                questions={questions}
                scores={scores}
                patientId={patientId}
                imageId={imageId}
                shareAnnotations={shareAnnotations}
                studyInstanceUID={studyUids[currentPatientIndex]}
                // Multi-patient navigation props
                patientIds={patientIds}
                currentPatientIndex={currentPatientIndex}
                onPatientChange={handlePatientChange}
                isNavigating={isNavigating}
                // Per-study toggles for which navbar buttons to show
                viewerNavbarConfig={viewerNavbarConfig}
                // Called before navigating away so trame can flush its state
                onBeforeLeave={isTrame ? handleBeforeLeave : null}
                // Toggle vertical orientation for arthrex-style 2D stacks
                // whose raw pixel data is anatomically inverted.
                onToggleTrameOrientation={isTrame ? handleToggleOrientation : null}
            />

            <div className={`dv-viewer-content ${showReport ? 'with-report' : ''}`}>
                <div className="dv-viewer-main" ref={containerRef}>
                    {viewerUrl && authReady ? (
                        <iframe
                            ref={iframeRef}
                            key={`viewer-${currentPatientIndex}`}
                            src={viewerUrl}
                            title="Medical Image Viewer"
                            className="dv-viewer-iframe"
                            referrerPolicy="no-referrer"
                            allow="fullscreen"
                            onLoad={handleIframeLoad}
                        />
                    ) : viewerUrl && !authReady ? (
                        <div className="dv-viewer-empty">
                            <i className="fa fa-spinner fa-spin"></i>
                            <p>Loading viewer...</p>
                        </div>
                    ) : (
                        <div className="dv-viewer-empty">
                            <i className="fa fa-image"></i>
                            <p>No viewer URL provided.</p>
                            <small>Please launch a viewer from the study dashboard.</small>
                        </div>
                    )}
                </div>

                {showReport && (
                    <div className="dv-viewer-report">
                        <ReportPanel
                            reportUrl={reportUrl}
                            onClose={handleCloseReport}
                        />
                    </div>
                )}
            </div>
        </div>
    );
};

export default DvViewer;
